import { useMemo, useState } from 'react';
import { useAgentUsersQuery, useCreateAgentMutation } from 'hooks/useAgents';
import { agentRealName } from 'types/agents';
import type { AgentOffice } from 'types/agents';
import { Modal } from './Modal';
import { MaxChatsStepper, OfficeSelect } from './AgentFields';

export function AddAgentModal({ onClose }: { onClose: () => void }) {
  const { data: users = [] } = useAgentUsersQuery();
  const create = useCreateAgentMutation();
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [alias, setAlias] = useState('');
  const [office, setOffice] = useState<AgentOffice>('front');
  const [maxChats, setMaxChats] = useState(3);

  const candidates = useMemo(
    () =>
      users
        .filter((u) => !u.isChatAgent)
        .filter((u) =>
          `${agentRealName(u)} ${u.email ?? ''}`.toLowerCase().includes(query.toLowerCase()),
        ),
    [users, query],
  );

  const submit = async () => {
    if (!userId || !alias.trim()) return;
    await create.mutateAsync({ userId, alias: alias.trim(), office, maxChats });
    onClose();
  };

  return (
    <Modal
      title="Lägg till agent"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!userId || !alias.trim() || create.isPending}
            className="rounded-lg bg-gray-950 px-3 py-1.5 text-sm font-medium text-white disabled:bg-gray-300"
          >
            Lägg till agent
          </button>
        </>
      }
    >
      <p className="mb-2 text-xs text-gray-500">Välj en inloggning och ge hen ett chattalias.</p>
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Sök användare…"
        className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
      />
      <div className="mb-4 max-h-44 overflow-y-auto rounded-lg border border-gray-100">
        {candidates.length === 0 ? (
          <div className="p-3 text-xs text-gray-400">Inga användare att lägga till.</div>
        ) : (
          candidates.map((u) => (
            <button
              key={u.userId}
              type="button"
              onClick={() => setUserId(u.userId)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left transition ${
                userId === u.userId ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <span className="min-w-0 truncate text-sm text-gray-800">
                {agentRealName(u)}
                <span className="ml-2 text-xs text-gray-400">{u.email}</span>
              </span>
              {userId === u.userId && <span className="text-xs text-blue-600">vald</span>}
            </button>
          ))
        )}
      </div>

      {userId && (
        <>
          <label className="mb-1 block text-sm font-medium text-gray-700">Chattalias</label>
          <input
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            placeholder="t.ex. Karl"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
          />
          <p className="mb-4 mt-1.5 text-xs text-gray-500">
            Namnet kunden ser – aldrig det riktiga.
          </p>
          <div className="flex gap-6">
            <OfficeSelect value={office} onChange={setOffice} />
            <MaxChatsStepper value={maxChats} onChange={setMaxChats} />
          </div>
        </>
      )}
    </Modal>
  );
}
